import { useEffect, useMemo, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import useCombinedTranscriptions from '@shared/hooks/useCombinedTranscriptions';
import { CallActors } from '@shared/constants/recipient-status.constants';
import { ToolCallData } from '@shared/services/conversationApiService/types';
import { parseToolCallPacket } from '@widgets/ToolCallCard/toolCallCard.utils';
import {
  dropDuplicatedSupervisorMessages,
  useSupervisorTranscriptions,
} from './useSupervisorTranscriptions';

interface TestingConversationViewMessageBase {
  id: string;
  actor: string;
  createdAt: string;
  updatedAt: string;
  startTime?: number;
  endTime?: number;
  firstReceivedTime?: number;
  lastReceivedTime?: number;
}

interface TestingTextMessage extends TestingConversationViewMessageBase {
  message: string;
  type?: 'text';
  toolCall?: never;
}

interface TestingToolCallMessage extends TestingConversationViewMessageBase {
  type: 'tool_call';
  toolCall: ToolCallData;
}

export type TestingConversationViewMessage = TestingTextMessage | TestingToolCallMessage;

const ACTOR_BY_ROLE = {
  assistant: CallActors.AGENT,
  user: CallActors.CUSTOMER,
  supervisor: CallActors.SUPERVISOR,
} as const;

export const useTestingConversationMessages = (
  room?: Room,
  callId?: string | null,
): TestingConversationViewMessage[] => {
  const combinedTranscriptions = useCombinedTranscriptions();
  const supervisorTranscriptions = useSupervisorTranscriptions(callId);
  const [toolCalls, setToolCalls] = useState<TestingConversationViewMessage[]>([]);

  useEffect(() => {
    setToolCalls([]);
    if (!room) return;
    const handleData = (
      payload: Uint8Array,
      _participant?: unknown,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== 'conversation.tool_call') return;
      const toolCall = parseToolCallPacket(payload);
      if (!toolCall) return;

      const firstReceivedTime = Date.now();
      const receivedAt = new Date(firstReceivedTime).toISOString();

      setToolCalls((current) => {
        if (current.some((message) => message.id === `tool-${toolCall.function_call.call_id}`)) {
          return current;
        }

        return [
          ...current,
          {
            id: `tool-${toolCall.function_call.call_id}`,
            actor: CallActors.AGENT,
            createdAt: receivedAt,
            updatedAt: receivedAt,
            firstReceivedTime,
            type: 'tool_call',
            toolCall,
          },
        ];
      });
    };
    room.on(RoomEvent.DataReceived, handleData);
    return () => {
      room.off(RoomEvent.DataReceived, handleData);
    };
  }, [room]);

  return useMemo(() => {
    const liveMessages = combinedTranscriptions.map((segment, index) => ({
      id: `live-${segment.id || index}`,
      actor: ACTOR_BY_ROLE[segment.role],
      message: segment.text,
      createdAt: new Date(segment.firstReceivedTime).toISOString(),
      updatedAt: new Date(segment.firstReceivedTime).toISOString(),
      firstReceivedTime: segment.firstReceivedTime,
      lastReceivedTime: segment.lastReceivedTime,
    }));

    return [
      ...liveMessages,
      ...dropDuplicatedSupervisorMessages(supervisorTranscriptions, liveMessages),
      ...toolCalls,
    ].sort(
      (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
    );
  }, [combinedTranscriptions, supervisorTranscriptions, toolCalls]);
};
